import { useEffect, useState } from 'react';
import { CircleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import type { RootState } from '~/redux/store.ts';
import { useGetOrderById, useGetPackingList } from '~/api';
import {
  assertDefined,
  formatDateTime,
  hasText,
  getLinePiecePrices,
  isRandomTallyItem,
  roundTo6dp,
} from '~/utils';
import { BackButton, ClipSpinner } from '~/components/ui';
import { TarpStatus } from '~/components/ui/TarpStatus';
import { OrderProgressTrackerCard } from '~/components/cards/OrderProgessTrackerCard.tsx';
import { OrderDocumentsCard } from '~/pages/order-view/sections/OrderDocumentsCard';
import { ItemCard } from '~/components/cards/ItemCard';
import {
  type TransportMode,
  vehicleTypeToTransportMode,
} from '~/hooks/useTransportState';
import { OrderStatusBadge } from '~/components/ui/OrderStatusBadge';
import { BlockedOrderCard } from '~/pages/order-view/sections/BlockedOrderCard';
import { SellerOrderCallToAction } from '~/pages/order-view/sections/SellerCallToAction';
import { useShipOrderModal } from '~/pages/order-view/sections/useShipOrderModal';
import { SourcingPanel } from '~/pages/order-view/sections/SourcingPanel';
import { SellerShippingCard } from '~/pages/order-view/sections/SellerShippingCard';
import { SellerOrderSummaryCard } from '~/pages/order-view/sections/SellerOrderSummaryCard';
import { BuyingInformationCard } from '~/pages/order-view/sections/BuyingInformationCard';
import { TransportationSection } from '~/pages/checkout/sections/TransportationSection';
import { OrderInternationalShippingCard } from '~/pages/order-view/sections/OrderInternationalShippingCard';
import type { OrderDocumentType } from '~/utils/documentType';
import {
  computeOrderItemsTotal,
  computePackedItemsTotal,
  getGradeInformation,
  getListedPricePerUnit,
  getVehiclePayload,
  groupItemsByTally,
  getPlatformProductDimensions,
} from '~/utils/order';
import { unitsPerPiece } from '~/utils/unitConversion';
import { formatAddressText, hasCoordinates } from '~/utils/addressFormat';
import { DeliveryAddressMapToggle } from '~/components/common/DeliveryAddressMapToggle';
import { PackingListSection } from '~/pages/order-view/sections/PackingListSection';
import { useOrderStatusChangeToast } from '~/hooks/useOrderStatusChangeToast';

export function SellerOrderView({ orderId }: Readonly<{ orderId: string }>) {
  const { t } = useTranslation('order');
  const { t: tOrderBuilder } = useTranslation('order-builder');
  const [searchParams] = useSearchParams();
  const rawReturnPath = searchParams.get('returnPath');
  const returnPath =
    rawReturnPath?.startsWith('/') && !rawReturnPath.startsWith('//')
      ? rawReturnPath
      : '/seller-orders';

  const { data, isLoading, isError, error } = useGetOrderById(orderId);
  const { data: packingListData } = useGetPackingList(orderId);
  const [activeItemsTab, setActiveItemsTab] = useState<'ordered' | 'packed'>(
    'ordered'
  );
  useOrderStatusChangeToast(data?.order?.status);
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const isTrader = role === 'TRADER';
  const { shipModal, openShipModal } = useShipOrderModal(orderId);

  useEffect(() => {
    const uploaded = data?.order?.documents.some(
      (doc) =>
        doc.documentType === 'PACKING_LIST' &&
        doc.uploadType === 'SELLER_UPLOAD'
    );
    if (uploaded) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- CR-01: reset tab to ordered when a PL upload supersedes native PL; data dependency prevents cascading re-runs
      setActiveItemsTab('ordered');
    }
  }, [data]);

  if (isLoading) return <ClipSpinner />;
  if (isError)
    return <div>{t('order.loading_error', { message: error.message })}</div>;

  assertDefined(data?.order, 'Order data should be defined');
  const order = data.order;

  const packingList = packingListData?.packingList ?? null;

  const orderDocuments: OrderDocumentType[] = order.documents;
  const hasUploadedPackingList = order.documents.some(
    (doc) =>
      doc.documentType === 'PACKING_LIST' && doc.uploadType === 'SELLER_UPLOAD'
  );

  const hasSuggestedPrice =
    order.status !== 'DRAFT' &&
    order.items.some(
      (item) => item.suggestedPrice != null && item.suggestedPrice.amount > 0
    );

  const isTraderSourcing =
    isTrader && (order.status === 'CONFIRMED' || order.isBrokered);

  const currency = order.currency ?? 'USD';

  const originalTotal = computeOrderItemsTotal(order.items);
  const packedTotal =
    packingList != null ? computePackedItemsTotal(packingList.items) : 0;
  const variance = packedTotal - originalTotal;

  const shippingDestination = order.deliveryAddress
    ? {
        shippingLocationName: t('order.delivery_address'),
        addressLine1:
          order.deliveryAddress.addressLine1 ??
          formatAddressText(order.deliveryAddress),
        addressLine2: order.deliveryAddress.addressLine2,
        city: order.deliveryAddress.city,
        region: order.deliveryAddress.region,
        zipCode: order.deliveryAddress.zipCode,
        isCoordinates:
          !order.deliveryAddress.addressLine1 &&
          hasCoordinates(order.deliveryAddress),
      }
    : order.shippingLocation
      ? {
          shippingLocationName: order.shippingLocation.name,
          addressLine1: order.shippingLocation.facility.address.addressLine1,
          addressLine2: order.shippingLocation.facility.address.addressLine2,
          city: order.shippingLocation.facility.address.city,
          region: order.shippingLocation.facility.address.region,
          zipCode: order.shippingLocation.facility.address.zipCode,
        }
      : null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      <BackButton
        link={returnPath}
        label={
          returnPath === '/dashboard'
            ? t('order.back_to_dashboard_label')
            : t('order.back_button_label')
        }
      />
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <h1
            className="text-2xl text-neut-prim font-semibold"
            data-testid="order-heading"
          >
            {t('order.seller_order_id', {
              orderId: hasText(order.purchaseOrderNumber)
                ? order.purchaseOrderNumber
                : order.id,
            })}
          </h1>
          <p className="text-sm text-gray-600">
            {t('order.seller_order_placed_on', {
              orderDate: formatDateTime(order.createdAt),
            })}
          </p>
        </div>
        <OrderStatusBadge status={order.status} size="small" />
      </div>
      <div className="bg-neut-bg-alt p-6 rounded-lg flex flex-col gap-4 border border-neut-200 shadow-sm">
        <OrderProgressTrackerCard order={order} />
        {order.status === 'BLOCKED' && (
          <BlockedOrderCard
            orderId={order.id}
            blockMessages={order.blockMessages}
            canReturnToDraft={order.canReturnToDraft}
          />
        )}
        <SellerOrderCallToAction
          orderId={order.id}
          orderStatus={order.status}
          awaitingPurchaseOrderNumber={order.awaitingPurchaseOrderNumber}
          canReturnToDraft={order.canReturnToDraft}
          hasSuggestedPrice={hasSuggestedPrice}
          hideShipButton={isTraderSourcing}
        />
        {isTraderSourcing && (
          <>
            {shipModal}
            <SourcingPanel order={order} onShipFromInventory={openShipModal} />
          </>
        )}
        {order.status === 'CONFIRMED' &&
          packingList == null &&
          !hasUploadedPackingList && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <CircleAlert
                size={20}
                className="text-amber-600 flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  {t('order.packing_list.required_warning_title')}
                </p>
                <p className="text-sm text-amber-700">
                  {t('order.packing_list.required_warning_description')}
                </p>
              </div>
            </div>
          )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-neut-prim font-semibold">
                {t('order.order_items')}
              </h2>
              <TarpStatus tarp={order.tarp} />
            </div>
            {(packingList != null || order.status === 'CONFIRMED') &&
              !hasUploadedPackingList && (
                <div className="flex border-b border-gray-100 px-6">
                  <button
                    type="button"
                    onClick={() => setActiveItemsTab('ordered')}
                    className={`py-3 px-4 text-sm font-medium border-b-2 -mb-px ${
                      activeItemsTab === 'ordered'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('order.items_tab_ordered')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveItemsTab('packed')}
                    className={`py-3 px-4 text-sm font-medium border-b-2 -mb-px ${
                      activeItemsTab === 'packed'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('order.items_tab_packed')}
                  </button>
                </div>
              )}
            {activeItemsTab === 'ordered' &&
              (() => {
                const vehicleId = order.transportVehicle?.id ?? '';
                const vehicleLabel: TransportMode | undefined =
                  order.transportVehicle
                    ? vehicleTypeToTransportMode(
                        order.transportVehicle.vehicleType
                      )
                    : undefined;
                const { tallyGroups, regularItems } = groupItemsByTally(
                  order.items
                );
                const renderItem = (item: (typeof order.items)[0]) => {
                  const ppl = item.platformProductsLengths;
                  const vehiclePayload = getVehiclePayload(
                    ppl.platformProductVehicleCapacities,
                    vehicleId
                  );
                  const dims = getPlatformProductDimensions(
                    ppl.platformProduct,
                    ppl.length,
                    ppl.isNominal
                  );
                  const hasDims =
                    dims.lengthInches > 0 &&
                    dims.widthInches > 0 &&
                    dims.thicknessInches > 0;
                  let vehiclePiecesCount: number | undefined;
                  const isRandomTally = isRandomTallyItem(item);
                  const totalPieces: number | undefined = isRandomTally
                    ? undefined
                    : item.quantityStored.value;
                  const { listedPricePerPiece, suggestedPerPiece } =
                    getLinePiecePrices(item, order.incoTerm);
                  const pricePerPiece: number | undefined =
                    listedPricePerPiece != null
                      ? roundTo6dp(listedPricePerPiece.amount)
                      : undefined;
                  // RAW (unrounded) per-piece amount — used ONLY for total arithmetic so the
                  // multiply-first-round-once contract matches calculateOrderTotals. The
                  // 6dp-rounded `pricePerPiece` above stays reserved for display text.
                  const pricePerPieceRaw: number | undefined =
                    listedPricePerPiece?.amount;
                  const listedPrice = getListedPricePerUnit(
                    item,
                    order.incoTerm
                  );
                  if (hasDims) {
                    try {
                      if (vehiclePayload && vehiclePayload.value > 0) {
                        const uppV = unitsPerPiece(
                          vehiclePayload.unit,
                          dims.lengthInches,
                          dims.widthInches,
                          dims.thicknessInches
                        );
                        if (uppV > 0)
                          vehiclePiecesCount = vehiclePayload.value / uppV;
                      }
                    } catch {
                      // unsupported unit combination
                    }
                  }
                  return (
                    <ItemCard
                      key={ppl.id}
                      productName={ppl.platformProduct.name || 'not found'}
                      itemQuantity={item.quantity.value}
                      unit={item.quantity.unit}
                      pricePerUnit={listedPrice.amount}
                      currency={listedPrice.currency}
                      length={ppl.lengthDisplayValue || 'N/A'}
                      gradeInformation={getGradeInformation(ppl)}
                      gradeLabel={t('order.grade')}
                      buyCode={item.buyCode}
                      suggestedPrice={
                        order.status === 'PENDING_SELLER_CONFIRMATION'
                          ? item.suggestedPrice?.amount
                          : undefined
                      }
                      vehiclePayload={vehiclePayload}
                      vehicleLabel={vehicleLabel}
                      piecesPerBundle={{
                        value: item.effectivePiecesPerBundle,
                        unit: 'PCS',
                      }}
                      vehiclePiecesCount={vehiclePiecesCount}
                      totalPieces={totalPieces}
                      pricePerPiece={pricePerPiece}
                      pricePerPieceRaw={pricePerPieceRaw}
                      suggestedPricePerPiece={
                        suggestedPerPiece != null
                          ? roundTo6dp(suggestedPerPiece.amount)
                          : undefined
                      }
                      suggestedPricePerPieceRaw={suggestedPerPiece?.amount}
                    />
                  );
                };
                return (
                  <>
                    {Array.from(tallyGroups.entries()).map(
                      ([groupKey, group]) => (
                        <div
                          key={groupKey}
                          className="border border-status-info-200 rounded-lg overflow-hidden mb-4 last:mb-0"
                        >
                          <div className="bg-status-info-50 px-4 sm:px-6 py-1.5 border-b border-status-info-200">
                            <span className="text-xs font-semibold text-status-info-800">
                              {tOrderBuilder(
                                group.isRandomTally
                                  ? 'order_builder.filters.random_tally'
                                  : 'order_builder.filters.canned_tally'
                              )}
                            </span>
                          </div>
                          {group.items.map(renderItem)}
                        </div>
                      )
                    )}
                    {regularItems.map(renderItem)}
                  </>
                );
              })()}
            {activeItemsTab === 'packed' && (
              <PackingListSection
                orderId={orderId}
                orderItems={order.items}
                orderStatus={order.status}
                currency={currency}
              />
            )}
          </div>
          {shippingDestination && (
            <SellerShippingCard
              contact={order.buyer.firstName}
              {...shippingDestination}
              mapToggle={
                <DeliveryAddressMapToggle
                  address={
                    order.deliveryAddress ??
                    order.shippingLocation?.facility.address
                  }
                />
              }
            />
          )}
          {order.isInternational ? (
            <OrderInternationalShippingCard order={order} />
          ) : (
            order.status !== 'DRAFT' &&
            order.incoTerm && (
              <TransportationSection
                items={order.items}
                incoTerm={order.incoTerm}
                vehicle={order.transportVehicle}
              />
            )
          )}
        </div>
        <div className="lg:col-span-1 space-y-6">
          <BuyingInformationCard
            buyerName={order.buyer.firstName}
            companyName={order.buyingOrganization.name}
            contact={order.buyerContact ?? null}
          />
          <SellerOrderSummaryCard
            items={order.items}
            status={order.status}
            currency={currency}
            incoTerm={order.incoTerm}
          />
        </div>
      </div>

      <OrderDocumentsCard
        orderId={orderId}
        documents={orderDocuments}
        packingList={packingList}
        orderItems={order.items}
        originalTotal={originalTotal}
        packedTotal={packedTotal}
        variance={variance}
        currency={currency}
      />
    </div>
  );
}
